import { ChapterContent } from '../../../types';
import { h, p, list, checklist, tip, warning } from '../blocks';
import { MARINE_WORLDS_EXPANSION_ID } from '../marine-worlds/expansion';

/**
 * Content Rewrite 3.0 — ONE menu chapter for Marine Worlds (Zwierzęta Wodne).
 * Internal headings only; no sub-items in the left menu.
 * Alternative action-card side I/II texts remain unverified — do not invent.
 */
export const ARK_NOVA_MARINE_CHAPTER_TITLE = 'Zwierzęta Wodne';
/** @deprecated alias — prefer ARK_NOVA_MARINE_CHAPTER_TITLE */
export const MARINE_WORLDS_CHAPTER_TITLE = ARK_NOVA_MARINE_CHAPTER_TITLE;

/** Former left-menu titles kept for deep-links / findChapterIndexByTitle. */
export const arkNovaMarineLegacyTitles: readonly string[] = [
  'ZW: Co dodaje rozszerzenie',
  'ZW: Integracja komponentów',
  'ZW: Zmiany przygotowania gry',
  'ZW: Zwierzęta morskie',
  'ZW: Akwaria',
  'ZW: Rafa',
  'ZW: Alternatywne karty akcji',
  'ZW: Nowe karty i projekty',
  'ZW: Nowe żetony premii',
  'ZW: Przerwa z dodatkiem',
  'ZW: Koniec gry z dodatkiem',
  'ZW: Pierwsza partia z dodatkiem',
  'ZW: Checklista zagrania zwierzęcia morskiego',
  'ZW: Najczęstsze błędy dodatku',
  'ZW: Reguła pierwszeństwa',
];

export function isArkNovaMarineLegacyTitle(title: string): boolean {
  return arkNovaMarineLegacyTitles.includes(title);
}

export const arkNovaMarineChapters: ChapterContent[] = [
  {
    title: ARK_NOVA_MARINE_CHAPTER_TITLE,
    requiredAddons: [MARINE_WORLDS_EXPANSION_ID],
    content: [
      warning(
        'Status dodatku: PARTIAL. Zasady systemowe (akwaria, Rafa, draft, integracja) są opisane poniżej. Dokładne teksty stron I/II wszystkich 20 alternatywnych kart akcji nie są w lokalnym materiale referencyjnym — wdrożenie ich opisów oczekuje na ten materiał. Nie zgaduj efektów kart.'
      ),

      h('Co dodaje rozszerzenie', 2),
      p(
        'Zwierzęta Wodne (Marine Worlds) dodają klasę zwierząt morskich, małe i duże akwaria, zdolność Rafa, nową uczelnię z wyborem ikony klasy, po cztery alternatywne wersje każdej z pięciu kart akcji, nowe karty zoo/projektów/punktacji, dodatkowe żetony premii oraz zamienniki części elementów podstawki.'
      ),
      p(
        'Po co: rozszerza ścieżki budowy i punktacji o oceanarium oraz różnicuje zestawy akcji bez zmiany liczby typów (nadal pięć kart na gracza).'
      ),

      h('Integracja komponentów', 2),
      list([
        'Wtasuj nowe karty zoo do odpowiednich kart podstawki.',
        'Wtasuj nowe karty punktacji końcowej.',
        'Dodaj nowe żetony premii do puli.',
        'Dodaj nową uczelnię do zasobów uczelni.',
        'Przygotuj małe i duże akwaria.',
        'Przygotuj zestawy alternatywnych kart akcji oddzielnie według typów.',
        'Usuń z gry elementy podstawki, dla których dodatek zawiera bezpośrednie zamienniki, zgodnie z listą komponentów dodatku.',
        'Nie mieszaj alternatywnych kart akcji jak zwykłej talii — są wybierane podczas przygotowania graczy.',
      ]),

      h('Zmiany przygotowania gry', 2),
      p(
        'Wykonaj przygotowanie podstawowe, potem zmiany dodatku. Nowa uczelnia: połóż kafel na najniższym wolnym miejscu uczelni, wybierz jedną klasę zwierząt, otrzymaj jej ikonę zgodnie z elementem, dobierz zwierzę wskazanej klasy według instrukcji uczelni i rozpatrz pozostałe premie kafla. Wybrana ikona jest stała, o ile element nie mówi inaczej.'
      ),
      checklist(
        'mw-alt-draft',
        [
          'Przygotuj alternatywne karty wszystkich pięciu typów.',
          'Każdy gracz otrzymuje zestaw kart do wyboru zgodnie z oficjalną procedurą draftu.',
          'Z otrzymanych kart wybiera dwie alternatywne karty akcji.',
          'Zastępuje nimi odpowiednie dwie podstawowe karty.',
          'Pozostałe trzy typy pozostają w wersji podstawowej.',
          'Wszystkie pięć kart rozpoczyna stroną I do góry.',
          'Rozmieść je tak jak w podstawce: Zwierzęta w pozycji 1, pozostałe losowo, chyba że wariant startowy mówi inaczej.',
        ],
        'Draft alternatywnych kart'
      ),
      p(
        'Nowe karty są częścią zwykłej talii zoo — nie tworzą osobnej talii morskiej. Wystawka działa jak w podstawce.'
      ),

      h('Zwierzęta morskie', 2),
      p(
        'Zwierzę morskie to pełnoprawna klasa: daje ikonę morską, może być wymagane przez projekty i punktację, zwykle wymaga akwarium zamiast standardowego wybiegu, może mieć Rafę oraz wymagania wody, uczelni, reputacji, kontynentów i ikon.'
      ),
      checklist(
        'mw-play-marine-animal-inline',
        [
          'Sprawdź wymagania.',
          'Wybierz właściwe akwarium z wolną pojemnością.',
          'Zapłać koszt.',
          'Zastosuj zniżki.',
          'Umieść kartę w zoo.',
          'Oznacz zajętą pojemność akwarium.',
          'Zdobądź atrakcyjność.',
          'Rozpatrz zdolność.',
          'Uruchom efekty Rafy, jeżeli są spełnione.',
        ],
        'Zagranie zwierzęcia morskiego'
      ),
      p(
        'Ikona właśnie zagrywanego zwierzęcia nie spełnia jego własnego wymagania, chyba że karta mówi inaczej. Ogród partnerski obniża koszt według kontynentu, nie według klasy morskiej.'
      ),

      h('Akwaria', 2),
      p(
        'Akwaria są specjalnymi wybiegami w dwóch rozmiarach. Małe zajmuje wskazaną na kaflu liczbę heksów i ma ograniczoną pojemność; duże zajmuje więcej pól i ma większą łączną pojemność — często pod większe zwierzę albo kilka mniejszych. Buduje się je ulepszoną Budową, chyba że efekt pozwala inaczej. Dokładne rozmiary i pojemności odczytuj z fizycznych kafli — aplikacja ich nie zgaduje.'
      ),
      p(
        'Każde zwierzę morskie zużywa pojemność wskazaną na karcie. Po zagraniu zaznacz zajęte miejsca zgodnie z systemem kafla. Nie przekraczaj pojemności, nie traktuj akwarium jak standardowego wybiegu, nie umieszczaj zwierząt niemorskich bez wyraźnego efektu i nie dziel jednego zwierzęcia między dwa akwaria.'
      ),
      p(
        'Przykład przy stole: budujesz duże akwarium ulepszoną Budową, potem zagrywasz zwierzę morskie o pojemności 2 do akwarium z wolnymi 3 miejscami. Zaznaczasz 2 miejsca, bierzesz atrakcyjność i zdolność. Kontrprzykład: próba włożenia zwierzęcia morskiego do pustego standardowego wybiegu 5 albo rozłożenia jednego zwierzęcia na małe i duże akwarium.'
      ),
      p(
        'Dla dochodu kiosku stosuj zasadę kwalifikujących się przyległych budynków. Pojedyncze akwarium liczy się jako jeden budynek, niezależnie od liczby zwierząt w środku. Czy puste akwarium kwalifikuje się — patrz glosariusz wydania (FAQ dodatku).'
      ),

      h('Rafa', 2),
      p(
        'Rafa nie jest osobną akcją. Uruchamia ją zagranie właściwej karty morskiej (lub inne zdarzenie wskazane symbolem). Rozpatrujesz kwalifikujące się efekty Rafy w zoo według ikonografii; każdy najwyżej tyle razy, ile dopuszcza tekst. Kartę właśnie zagraną uwzględniasz zgodnie z momentem na karcie/w glosariuszu. Przy kilku efektach aktywny gracz wybiera kolejność i kończy jeden przed następnym. Nie traktuj korzyści Rafy jako nowego zagrania zwierzęcia — jedno zagranie to jedno zdarzenie uruchamiające, bez nieskończonej rekurencji.'
      ),
      p(
        'Przykład: masz dwa wcześniejsze zwierzęta z efektami Rafy. Zagrywasz trzecie spełniające warunek. Rozpatrujesz kwalifikujące się efekty po kolei, potem kontynuujesz pozostałe efekty zagrywanej karty. Rafa nie uruchamia się automatycznie w przerwie, chyba że konkretna karta tak stanowi.'
      ),

      h('Alternatywne karty akcji', 2),
      p(
        'Każda alternatywna karta zastępuje kartę tego samego typu, ma strony I i II, przesuwa się na pozycjach 1–5, przyjmuje X i ulepszenia. Tekst alternatywnej karty ma pierwszeństwo przed opisem standardowej wersji. Dodatkowa premia nie znosi kosztów i wymagań, chyba że wyraźnie tak stanowi.'
      ),
      list([
        'Karty — mogą zmieniać dobieranie, odrzucanie, wystawkę lub przerwę; stosuj tabelę z używanej karty.',
        'Budowa — mogą premiować budynki lub kolejność; legalne umieszczanie nadal obowiązuje.',
        'Zwierzęta — mogą premiować klasy (w tym morskie); nadal potrzebujesz wybiegu/akwarium i wymagań.',
        'Stowarzyszenie — mogą zmieniać dostęp do zadań; pracownicy wracają w przerwie.',
        'Sponsorzy — mogą modyfikować zagranie lub alternatywę przerwy; przelicznik z karty.',
      ]),
      warning(
        'Blocker: 20 konkretnych opisów stron I/II nie jest zaimplementowanych. Ekran listy kart pokazuje model danych ze statusem „oczekuje na zweryfikowaną transkrypcję”. Nie uzupełniaj efektów z pamięci.'
      ),

      h('Nowe karty, projekty i żetony premii', 2),
      p(
        'Nowe zwierzęta gra się akcją Zwierzęta, sponsorów Sponsorami, projekty Stowarzyszeniem, karty końcowe po grze. Ikona morska liczy się jak inna klasa przy wymaganiach projektów, o ile projekt nie mówi inaczej. Przy wypuszczeniu do natury: wybierz legalne zwierzę, zastosuj warunki, zwolnij wybieg lub pojemność (akwarium zostaje, jeśli są inne zwierzęta), skoryguj ikony i efekty stałe.'
      ),
      list([
        'Nowe żetony premii dodaj do wspólnej puli.',
        'Efekt żetonu rozpatruje się natychmiast po zdobyciu, chyba że ikona każe zachować go na później.',
        'Wybór klasy musi być jednoznacznie oznaczony.',
        'Niewykonalna część efektu przepada, chyba że reguła każe wybrać inną opcję.',
      ]),

      h('Przerwa i koniec gry z dodatkiem', 2),
      p(
        'Przerwę przeprowadzasz jak w podstawce. Dodatkowo sprawdź czasowe żetony z alternatywnych kart, dochody nowych kart, status akwariów przy kioskach oraz limity z nowej uczelni. Warunek końca gry się nie zmienia. Do punktacji dodaj nowe karty końcowe i efekty morskie/sponsorów/akwariów wynikające z kart. Same alternatywne karty akcji nie dają punktów, chyba że ich tekst to przewiduje.'
      ),

      h('Pierwsza partia z dodatkiem', 2),
      list([
        'Użyjcie map A.',
        'Włączcie karty dodatku do talii.',
        'Każdy wybiera dwie alternatywne karty akcji (różne typy).',
        'Przed startem pokażcie: ikonę morską, oba akwaria, pojemność, Rafę, nową uczelnię.',
        'Nie omawiajcie wszystkich 20 kart — każdy czyta tylko swoje dwie.',
        'Przy pierwszej karcie morskiej przejdźcie checklistę poniżej.',
      ]),

      h('Checklista zagrania zwierzęcia morskiego', 2),
      checklist(
        'mw-play-marine-animal',
        [
          'Czy karta jest w ręce albo legalnie dostępna z wystawki?',
          'Czy spełniam wszystkie wymagania?',
          'Czy mam akwarium właściwego typu?',
          'Czy akwarium ma wystarczającą wolną pojemność?',
          'Czy mam pieniądze po uwzględnieniu zniżek?',
          'Czy oznaczyłem zajętą pojemność?',
          'Czy zdobyłem atrakcyjność?',
          'Czy dodałem ikonę morską?',
          'Czy rozpatrzyłem zdolność natychmiastową?',
          'Czy uruchomiły się efekty Rafy?',
          'Czy uzupełniłem wystawkę?',
          'Czy sprawdziłem koniec gry?',
        ],
        'Zagranie zwierzęcia morskiego'
      ),

      h('Najczęstsze błędy dodatku', 2),
      list([
        'Umieszczanie zwierzęcia morskiego w standardowym wybiegu.',
        'Traktowanie każdego zwierzęcia w akwarium jako osobnego budynku przy kiosku.',
        'Przekraczanie pojemności albo dzielenie zwierzęcia między dwa akwaria.',
        'Uruchamianie Rafy przy dowolnym zwierzęciu lub w przerwie bez tekstu karty.',
        'Więcej niż dwie alternatywne karty albo dwa warianty tego samego typu.',
        'Stosowanie ogólnego opisu akcji zamiast tekstu alternatywnej karty.',
        'Usuwanie całego akwarium po wypuszczeniu jednego z kilku zwierząt.',
      ]),

      h('Reguła pierwszeństwa', 2),
      list([
        'Tekst konkretnej karty lub kafla.',
        'Oficjalny glosariusz Zwierząt Wodnych.',
        'Instrukcja dodatku.',
        'Oficjalny glosariusz podstawki.',
        'Instrukcja podstawowa.',
        'Ogólne objaśnienie w aplikacji.',
      ]),

      warning(
        'Typowy błąd: uznać dodatek za `ready` albo dopisać efekty 20 kart z pamięci. Status pozostaje partial.'
      ),
      tip(
        'Wyjątek i odsyłacz: procedury Budowy, Zwierząt, przerwy i końca gry z podstawy nadal obowiązują; ten rozdział dodaje tylko nadpisania dodatku. Ekran 20 kart prowadzi do modelu oczekującego na transkrypcję.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Jedna klasa morska, akwaria, Rafa, draft dwóch alternatyw, te same tory końca. P: Czy zwierzę morskie idzie do zwykłego wybiegu? O: Nie, bez efektu. P: Czy aplikacja zastępuje tekst alternatywnej karty? O: Nie — czeka na lokalny materiał. P: Czy dodatek zmienia trigger końca? O: Nie.'
      ),
      p('---'),
    ],
  },
];
